import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Star, PenLine } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../api';

export const StarRating = ({ value, onChange, size = 'w-8 h-8' }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-1" data-testid="star-rating">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          data-testid={`star-${n}`}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(n)}
          className="transition-transform hover:scale-110"
          aria-label={`${n} star${n > 1 ? 's' : ''}`}
        >
          <Star className={`${size} ${(hover || value) >= n ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
        </button>
      ))}
    </div>
  );
};

export const ReviewDialog = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState('');
  const [location, setLocation] = useState('');
  const [destination, setDestination] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (text.trim().length < 10) {
      toast.error('Please write at least 10 characters');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/reviews', { rating, text: text.trim(), location: location || null, destination: destination || null });
      toast.success('Thank you! Your review is now live.');
      onCreated(res.data);
      setOpen(false);
      setText(''); setLocation(''); setDestination(''); setRating(5);
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || 'Could not submit review');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="write-review-btn" className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white">
          <PenLine className="w-4 h-4 mr-2" /> Write a Review
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="review-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl">Share your experience</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div>
            <Label className="mb-2 block">Your rating</Label>
            <StarRating value={rating} onChange={setRating} />
          </div>
          <div>
            <Label htmlFor="review-text" className="mb-2 block">Your review *</Label>
            <Textarea data-testid="review-text-input" id="review-text" value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={600} placeholder="Tell other travelers about your trip..." className="border-2" required />
            <p className="text-xs text-gray-400 mt-1 text-right">{text.length}/600</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="review-location" className="mb-2 block">Your city</Label>
              <Input data-testid="review-location-input" id="review-location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Kuwait City" className="border-2" maxLength={80} />
            </div>
            <div>
              <Label htmlFor="review-destination" className="mb-2 block">Trip destination</Label>
              <Input data-testid="review-destination-input" id="review-destination" value={destination} onChange={(e) => setDestination(e.target.value)} placeholder="Kerala" className="border-2" maxLength={80} />
            </div>
          </div>
          <Button data-testid="review-submit-btn" type="submit" disabled={submitting} className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 text-white py-5">
            {submitting ? 'Posting...' : 'Post Review'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
